import { useEffect, useRef, useState } from "react";
import { Bold, Italic, Underline, Heading2, Heading3, List, ListOrdered, Quote, Link as LinkIcon, Image as ImageIcon, Minus, Code, Undo, Redo, Eraser, Loader2, Pilcrow } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, img } from "../../lib/api";

const BTN = "p-2 border border-transparent hover:border-black transition-colors disabled:opacity-40";
const ToolIcon = ({ I }) => <I className="h-4 w-4" />;

export function RichEditor({ value, onChange, testid = "rich-editor" }) {
  const ref = useRef();
  const fileRef = useRef();
  const [busy, setBusy] = useState(false);
  const [html, setHtml] = useState(false);
  useEffect(() => { if (ref.current && !html && ref.current.innerHTML !== (value || "")) ref.current.innerHTML = value || ""; }, [value, html]);
  const emit = () => onChange(ref.current.innerHTML);
  const cmd = (c, arg = null) => { ref.current.focus(); document.execCommand(c, false, arg); emit(); };
  const block = (tag) => cmd("formatBlock", tag);
  const link = () => { const u = window.prompt("Link URL (https://…)"); if (u) cmd("createLink", u); };
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const r = await api.post("/admin/upload", fd);
      const alt = window.prompt("Image alt text (describe the photo)") || "";
      ref.current.focus();
      document.execCommand("insertHTML", false, `<figure><img src="${img(r.data.url)}" alt="${alt.replace(/"/g, "&quot;")}" loading="lazy" />${alt ? `<figcaption>${alt}</figcaption>` : ""}</figure><p><br/></p>`);
      emit();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); fileRef.current.value = ""; }
  };
  const onPaste = (e) => {
    const f = [...(e.clipboardData?.files || [])].find((x) => x.type.startsWith("image/"));
    if (f) { e.preventDefault(); upload(f); }
  };
  const tools = [
    ["Paragraph", Pilcrow, () => block("p")], ["Heading 2", Heading2, () => block("h2")], ["Heading 3", Heading3, () => block("h3")], null,
    ["Bold", Bold, () => cmd("bold")], ["Italic", Italic, () => cmd("italic")], ["Underline", Underline, () => cmd("underline")], null,
    ["Bulleted list", List, () => cmd("insertUnorderedList")], ["Numbered list", ListOrdered, () => cmd("insertOrderedList")], ["Quote", Quote, () => block("blockquote")], ["Code block", Code, () => block("pre")], ["Divider", Minus, () => cmd("insertHorizontalRule")], null,
    ["Link", LinkIcon, link], ["Insert image", ImageIcon, () => fileRef.current.click()], null,
    ["Clear formatting", Eraser, () => cmd("removeFormat")], ["Undo", Undo, () => cmd("undo")], ["Redo", Redo, () => cmd("redo")],
  ];
  return (
    <div className="border border-[#E5E5E5] bg-white" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-0.5 border-b border-[#E5E5E5] p-1 sticky top-0 bg-white z-10">
        {tools.map((t, i) => t === null ? <span key={i} className="w-px h-6 bg-[#E5E5E5] mx-1" /> : (
          <button key={t[0]} type="button" title={t[0]} aria-label={t[0]} className={BTN} disabled={html || (t[0] === "Insert image" && busy)} onMouseDown={(e) => e.preventDefault()} onClick={t[2]} data-testid={`${testid}-${t[0].toLowerCase().replace(/\s+/g, "-")}`}>
            {t[0] === "Insert image" && busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ToolIcon I={t[1]} />}
          </button>
        ))}
        <button type="button" className={`ml-auto px-3 py-1.5 text-[11px] uppercase tracking-wider border ${html ? "bg-black text-white border-black" : "border-[#E5E5E5] hover:border-black"}`} onClick={() => setHtml(!html)} data-testid={`${testid}-html-toggle`}>HTML</button>
      </div>
      {html ? (
        <textarea className="w-full min-h-[360px] p-4 font-mono text-xs outline-none" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`${testid}-html`} />
      ) : (
        <div ref={ref} contentEditable suppressContentEditableWarning className="rte-body prose-bw p-4 sm:p-6" data-placeholder="Start writing your story… Paste or insert images anywhere." onInput={emit} onBlur={emit} onPaste={onPaste} data-testid={`${testid}-body`} />
      )}
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={(e) => upload(e.target.files[0])} />
    </div>
  );
}
